import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { Sun, Copy, Check, RefreshCw, Heart } from 'lucide-react'
import { Button } from '@/components/ui/button'
import api from '@/api/client'
import { toast } from '@/components/ui/toaster'

interface Affirmation {
  text: string
  author: string
  date: string
}

export default function DailyLight() {
  const [affirmation, setAffirmation] = useState<Affirmation | null>(null)
  const [randomAffirmation, setRandomAffirmation] = useState<Affirmation | null>(null)
  const [copied, setCopied] = useState(false)
  const [loading, setLoading] = useState(true)
  const { t, i18n } = useTranslation()

  const fetchDaily = async () => {
    setLoading(true)
    try {
      const lang = i18n.language === 'tr' ? 'tr' : 'en'
      const res = await api.get(`/daily-light?lang=${lang}`)
      setAffirmation(res.data)
    } catch {
      setAffirmation({
        text: t('dailyLight.fallbackText'),
        author: t('dailyLight.author'),
        date: new Date().toISOString().split('T')[0]
      })
    } finally {
      setLoading(false)
    }
  }

  const fetchRandom = async () => {
    try {
      const lang = i18n.language === 'tr' ? 'tr' : 'en'
      const res = await api.get(`/daily-light/random?lang=${lang}`)
      setRandomAffirmation(res.data)
    } catch {
      setRandomAffirmation({
        text: t('dailyLight.randomFallbackText'),
        author: t('dailyLight.author'),
        date: ''
      })
    }
  }

  useEffect(() => {
    fetchDaily()
    fetchRandom()
  }, [i18n.language])

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text)
    setCopied(true)
    toast({ title: t('dailyLight.copied') })
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-12">
      <div className="text-center mb-12">
        <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
          <Sun className="w-8 h-8 text-primary" />
        </div>
        <h1 className="font-display text-3xl font-bold mb-2">{t('dailyLight.title')}</h1>
        <p className="text-muted-foreground">{t('dailyLight.subtitle')}</p>
      </div>

      <AnimatePresence mode="wait">
        {!loading && affirmation && (
          <motion.div
            key={affirmation.date}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="relative p-8 md:p-12 rounded-3xl bg-gradient-to-br from-primary/20 via-primary/10 to-transparent border border-primary/20 mb-8"
          >
            <div className="absolute top-4 right-4 flex gap-2">
              <button
                onClick={() => handleCopy(affirmation.text)}
                className="p-2 rounded-lg bg-background/50 hover:bg-background transition-colors"
              >
                {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
            <div className="text-6xl text-primary/20 font-display mb-4">"</div>
            <p className="text-xl md:text-2xl font-medium leading-relaxed mb-6">
              {affirmation.text}
            </p>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">— {affirmation.author}</span>
              <span className="text-xs text-muted-foreground">{affirmation.date}</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {randomAffirmation && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="p-6 rounded-2xl bg-card border border-border"
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-2">{t('dailyLight.anotherLight')}</h3>
              <p className="text-lg font-medium">{randomAffirmation.text}</p>
            </div>
            <button
              onClick={() => handleCopy(randomAffirmation.text)}
              className="p-2 rounded-lg hover:bg-secondary transition-colors shrink-0"
            >
              <Copy className="w-4 h-4" />
            </button>
          </div>
        </motion.div>
      )}

      <div className="flex justify-center mt-8 gap-3">
        <Button variant="outline" onClick={fetchRandom} className="rounded-full">
          <RefreshCw className="w-4 h-4 mr-2" /> {t('dailyLight.newMessage')}
        </Button>
        <Button onClick={() => {
          const text = affirmation?.text || randomAffirmation?.text
          if (text) {
            const shareData = { title: t('dailyLight.shareTitle'), text }
            if (navigator.share) navigator.share(shareData)
            else handleCopy(text)
          }
        }} className="rounded-full">
          <Heart className="w-4 h-4 mr-2" /> {t('dailyLight.share')}
        </Button>
      </div>
    </div>
  )
}
